import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Button } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import IncomeService from "../../../Services/income/IncomeService";
import SpinnerLoader from "../../Loaders/Spinner";
import { formatYoutubeMonth } from "./IncomeListHelpers";
import "./youtube-income-details.scss";

function formatMoney(value, currency = "") {
  const raw = value === null || value === undefined || value === "" ? 0 : value;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  const amount = Number.isNaN(n) ? "0.00" : n.toFixed(2);
  const code = currency ? String(currency).trim() : "";
  return code ? `${amount} ${code}` : amount;
}

function formatPeriod(start, end) {
  if (!start && !end) return "—";
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString();
}

const YoutubeIncomeDetails = () => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const [videoSearch, setVideoSearch] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["youtubeIncomeDetails", type, id],
    queryFn: () => IncomeService.getYoutubeIncomeDetails({ type, id }),
    enabled: !!type && !!id,
  });

  const details = data?.data;
  const currency = details?.currencies ? String(details.currencies).trim() : "";
  const isArtistsRevenue = type === "artists_revenue";

  const videos = Array.isArray(details?.videos) ? details.videos : [];

  const filteredVideos = useMemo(() => {
    const term = videoSearch.trim().toLowerCase();
    if (!term) return videos;
    return videos.filter((video) =>
      String(video.title || "")
        .toLowerCase()
        .includes(term)
    );
  }, [videos, videoSearch]);

  if (isLoading) return <SpinnerLoader />;
  if (isError) return <div className="alert alert-danger m-3">Error: {error.message}</div>;

  const artistName = details?.artist_name || details?.title || "YouTube Income";
  const periodLabel = formatYoutubeMonth(details?.month);
  const hasUsTax = Number(details?.us_tax_amount ?? 0) > 0;
  const channel = details?.channel ?? null;
  const channelTitle = channel?.title || null;
  const channelHandle = channel?.custom_url
    ? channel.custom_url.startsWith("@")
      ? channel.custom_url
      : `@${channel.custom_url.replace(/^@/, "")}`
    : null;

  return (
    <div className="rd-youtube-income">
      <Breadcrumbs
        parent="Income"
        title="YouTube revenue"
        mainTitle={artistName}
        buttons={[
          <Button key="back" size="sm" color="primary" outline onClick={() => navigate(-1)}>
            <i className="fa fa-arrow-left me-1" />
            Back to income
          </Button>,
        ]}
      />

      <Container fluid>
        <section className="rd-youtube-income__hero">
          <div className="rd-youtube-income__hero-main">
            <span className="rd-youtube-income__badge">
              <i className="fa fa-youtube-play" aria-hidden="true" />
              YouTube
            </span>
            <h1 className="rd-youtube-income__artist">{artistName}</h1>
            <p className="rd-youtube-income__period">{periodLabel}</p>
            <div className="rd-youtube-income__meta">
              <span>
                <i className="fa fa-calendar me-1" aria-hidden="true" />
                {formatPeriod(details?.period_start, details?.period_end)}
              </span>
              <span>
                <i className="fa fa-building-o me-1" aria-hidden="true" />
                {details?.provider || "YouTube"}
              </span>
              {isArtistsRevenue ? (
                <span>
                  <i className="fa fa-bar-chart me-1" aria-hidden="true" />
                  Channel revenue
                </span>
              ) : null}
            </div>
          </div>

          {channel?.id || channel?.title || channel?.thumbnail_url ? (
            <div className="rd-youtube-income__channel">
              {channel.thumbnail_url ? (
                <img
                  src={channel.thumbnail_url}
                  alt={channelTitle || "YouTube channel"}
                  className="rd-youtube-income__channel-avatar"
                />
              ) : (
                <div className="rd-youtube-income__channel-avatar rd-youtube-income__channel-avatar--placeholder">
                  <i className="fa fa-youtube-play" aria-hidden="true" />
                </div>
              )}
              <div className="rd-youtube-income__channel-body">
                <span className="rd-youtube-income__channel-label">YouTube channel</span>
                {channel.url ? (
                  <a
                    href={channel.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rd-youtube-income__channel-name"
                  >
                    {channelTitle || channel.id}
                    <i className="fa fa-external-link" aria-hidden="true" />
                  </a>
                ) : (
                  <span className="rd-youtube-income__channel-name rd-youtube-income__channel-name--static">
                    {channelTitle || channel.id || "Connected channel"}
                  </span>
                )}
                {channelHandle ? (
                  <span className="rd-youtube-income__channel-handle">{channelHandle}</span>
                ) : null}
                <div className="rd-youtube-income__channel-stats">
                  {channel.subscriber_count != null ? (
                    <span>{formatCount(channel.subscriber_count)} subscribers</span>
                  ) : null}
                  {channel.video_count != null ? (
                    <span>{formatCount(channel.video_count)} videos</span>
                  ) : null}
                  {channel.country ? <span>{channel.country}</span> : null}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="rd-youtube-income__stats">
          <div className="rd-youtube-income__stat rd-youtube-income__stat--primary">
            <span className="rd-youtube-income__stat-label">Total revenue</span>
            <span className="rd-youtube-income__stat-value rd-youtube-income__stat-value--accent">
              {formatMoney(details?.videos_total_revenue ?? details?.amount ?? 0, currency)}
            </span>
          </div>
          <div className="rd-youtube-income__stat">
            <span className="rd-youtube-income__stat-label">Net after tax</span>
            <span className="rd-youtube-income__stat-value">
              {formatMoney(details?.net_after_tax ?? details?.amount ?? 0, currency)}
            </span>
          </div>
          <div className="rd-youtube-income__stat">
            <span className="rd-youtube-income__stat-label">US tax</span>
            <span className="rd-youtube-income__stat-value rd-youtube-income__stat-value--sm">
              {formatMoney(details?.us_tax_amount ?? 0, currency)}
            </span>
            {hasUsTax ? (
              <span className="rd-youtube-income__stat-hint">15% withholding</span>
            ) : null}
          </div>
          <div className="rd-youtube-income__stat">
            <span className="rd-youtube-income__stat-label">Videos</span>
            <span className="rd-youtube-income__stat-value">{details?.videos_count ?? videos.length}</span>
          </div>
        </section>

        {hasUsTax ? (
          <section className="rd-youtube-income__tax-flow" aria-label="US tax breakdown">
            <div className="rd-youtube-income__tax-step">
              <span className="rd-youtube-income__tax-step-label">US revenue</span>
              <span className="rd-youtube-income__tax-step-value">
                {formatMoney(details?.original_us_revenue ?? 0, currency)}
              </span>
            </div>
            <span className="rd-youtube-income__tax-arrow" aria-hidden="true">
              <i className="fa fa-minus" />
            </span>
            <div className="rd-youtube-income__tax-step">
              <span className="rd-youtube-income__tax-step-label">US tax (15%)</span>
              <span className="rd-youtube-income__tax-step-value">
                {formatMoney(details?.us_tax_amount ?? 0, currency)}
              </span>
            </div>
            <span className="rd-youtube-income__tax-arrow" aria-hidden="true">
              <i className="fa fa-long-arrow-right" />
            </span>
            <div className="rd-youtube-income__tax-step rd-youtube-income__tax-step--result">
              <span className="rd-youtube-income__tax-step-label">Net after tax</span>
              <span className="rd-youtube-income__tax-step-value">
                {formatMoney(details?.net_after_tax ?? 0, currency)}
              </span>
            </div>
          </section>
        ) : null}

        <section className="rd-youtube-income__panel">
          <div className="rd-youtube-income__panel-head">
            <div>
              <h5>Video revenue breakdown</h5>
              <span className="rd-youtube-income__panel-count">
                {filteredVideos.length} of {videos.length} video{videos.length !== 1 ? "s" : ""}
              </span>
            </div>
            {videos.length > 0 ? (
              <label className="rd-youtube-income__search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search videos…"
                  value={videoSearch}
                  onChange={(event) => setVideoSearch(event.target.value)}
                  aria-label="Search videos"
                />
              </label>
            ) : null}
          </div>

          {videos.length === 0 ? (
            <div className="rd-youtube-income__empty">
              No videos found for this artist and period.
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="rd-youtube-income__empty">No videos match your search.</div>
          ) : (
            <div className="rd-youtube-income__table-wrap">
              <table className="rd-youtube-income__table">
                <thead>
                  <tr>
                    <th style={{ width: "3rem" }}>#</th>
                    <th>Video</th>
                    <th className="text-end">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVideos.map((video, index) => (
                    <tr key={video.id ?? `${video.video_youtube_id}-${index}`}>
                      <td>
                        <span className="rd-youtube-income__rank">{index + 1}</span>
                      </td>
                      <td>
                        {video.video_url ? (
                          <a
                            href={video.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className="rd-youtube-income__video-link"
                          >
                            {video.title || "Untitled"}
                            <i className="fa fa-external-link" aria-hidden="true" />
                          </a>
                        ) : (
                          video.title || "Untitled"
                        )}
                      </td>
                      <td className="text-end">
                        <span className="rd-youtube-income__amount">
                          {formatMoney(video.estimated_revenue ?? 0, currency)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </Container>
    </div>
  );
};

export default YoutubeIncomeDetails;
